import React from "react";
import { useLocation } from "react-router-dom";
import FooterOne from "../common/footer/FooterOne";
import HeaderOne from "../common/header/HeaderOne";
import SectionTitle from "../elements/section-title/SectionTitle";
import SEO from "../common/SEO";
import CounterUpTwo from "../component/counterup/CounterUpTwo";
import AboutThree from "../component/about/AboutThree";
import BreadCrumbOne from "../elements/breadcrumb/BreadCrumbOne";
import ClientSlider from "../component/client/ClientSlider";

const contentMapping = {
  "/terms-conditions": {
    title: "Terms & Conditions",
    description:
      "At UCWF, we are pleased to be your reliable partner in facilities management in the United Arab Emirates. Read these terms and conditions carefully; they spell out the responsibilities and privileges of UCWF and its customers, and your use of our services indicates your consent to them.",
    content: [
      {
        title: "Scope of Services",
        subcontent:
          "Residential, commercial, retail, and industrial properties can all benefit from UCWF's comprehensive facilities management services, which include cleaning, security, maintenance, and specialised equipment management. The contracted agreement will specify the exact nature of the service.",
      },
      {
        title: "Service Agreements",
        subcontent:
          "Formal agreements outlining the scope, duration, and cost of all UCWF services are required. Any changes to the agreement need to be signed by both parties and put in writing.",
      },
      {
        title: "Payment Terms",
        subcontent:
          "The service agreement specifies the fees that the client is obligated to pay. Please note that payments must be made by the specified date to avoid incurring late fees. UCWF has the authority to deduct or stop providing services in the event that payment is not received.",
      },
      {
        title: "Responsibilities of UCWF",
        subcontent:
          "UCWF guarantees timely and expert service delivery. We make sure that all rules, regulations, and safety protocols are followed. In order to maintain high standards of reliability and quality, UCWF may outsource some of its services to authorised third parties.",
      },
      {
        title: "Client Responsibilities",
        subcontent:
          "In order for UCWF to carry out its services, clients must grant us accurate information and allow us unfettered access to the property. Additional charges may be incurred if the client causes any delays.",
      },
      {
        title: "Liability and Limitations",
        subcontent:
          "No matter how hard UCWF tries, we can't pay for any kind of indirect or consequential damage that comes from service outages or delays. All claims for damages must be limited to the amount actually paid for the service in issue.",
      },
      {
        title: "Termination",
        subcontent:
          "Following the procedures specified in the contract, either party may give the other party written notice of termination of the service agreement. Quitting your job too soon could cost you.",
      },
      {
        title: "Confidentiality",
        subcontent:
          "All client information is kept confidential by UCWF, and clients are expected to do the same when it comes to the company's practices and service methodologies.",
      },
      {
        title: "Governing Law",
        subcontent:
          "All disputes shall be resolved in the courts of UAE, and the laws of the United Arab Emirates shall govern these terms and conditions.",
      },
      {
        title: "Amendments",
        subcontent:
          "UCWF may make any changes to these terms and conditions at any time. Any major changes will be communicated to clients in advance of their implementation.",
      },
    ],
  },
  "/privacy-policy": {
    title: "Privacy Policy",
    description:
      "Your privacy is important to UCWF, and we will do all in our power to keep the information you provide us secure. All information pertaining to your interactions with our services is gathered, used, stored, and safeguarded in accordance with this Privacy Policy.",
    content: [
      {
        title: "Information We Collect",
        subcontent:
          "Among the data that we might gather are personal details, property information, and usage data.",
      },
      {
        title: "Individual Details",
        subcontent:
          "Information requested for service contracts or enquiries, including name, email, and phone number, as well as billing details.",
      },
      {
        title: "Property Information",
        subcontent:
          "Information about the property that needs services, including its location and any particular needs.",
      },
      {
        title: "Usage Information",
        subcontent:
          "Information gathered through your use of our website or services, such as your IP address, browser type, and cookies, for statistical analysis.",
      },
      {
        title: "How We Use Your Information",
        subcontent:
          "Data is gathered to implement and supervise our services, improve customer service, complete transactions, enhance our platform, and comply with regulations.",
      },
      {
        title: "Information Sharing",
        subcontent:
          "Our organisation will not give, sell, or rent any details about you without your permission. We may share information with service providers and legal authorities when necessary.",
      },
      {
        title: "Data Security",
        subcontent:
          "To prevent the loss, misuse, or alteration of your data, we have put in place robust security measures including restricted access, encryption, and secure servers.",
      },
    ],
  },
  "/cookie-policy": {
    title: "Cookie Policy",
    description:
      "We use cookies at UCWF to improve your browsing experience and provide more personalized services. In this Cookie Policy, we lay out the basics of cookies, how we use them, and the options available to you regarding their usage.",
    content: [
      {
        title: "What Are Cookies?",
        subcontent:
          "Individuals access websites and store small text files on their devices. These files are called cookies. They help websites remember what you do and what you like, making browsing more manageable to your needs.",
      },
      {
        title: "How We Use Cookies",
        subcontent:
          "UCWF uses cookies to make the website work better, remember preferences, analyze traffic, and provide personalized content and ads.",
      },
      {
        title: "Different Kinds of Cookies We Use",
        subcontent:
          "We use essential cookies for basic functionality, performance cookies for analytics, functional cookies for preferences, and advertising cookies for targeted content.",
      },
      {
        title: "Third-Party Cookies",
        subcontent:
          "When UCWF works with trusted third-party service providers, like ad networks and analytics tools, they may put cookies on our website to help show relevant content.",
      },
      {
        title: "Managing Your Cookie Preferences",
        subcontent:
          "You can choose whether to accept cookies or not through browser settings. Note that disabling cookies may affect website functionality.",
      },
      {
        title: "What You Can Do",
        subcontent:
          "You can change browser settings, opt-out of targeted ads, and disable analytics tracking through browser add-ons.",
      },
      {
        title: "Updates to This Policy",
        subcontent:
          "UCWF may change this Cookie Policy if our business practices or new laws require it. Please check periodically for updates.",
      },
    ],
  },
};

const CookiePolicyPage = () => {
  const location = useLocation();
  const currentPath = location.pathname.replace(process.env.PUBLIC_URL, "");
  const pageContent = contentMapping[currentPath] || {
    title: "Page Not Found",
    content: "Sorry, the content you're looking for doesn't exist.",
  };

  return (
    <>
      <SEO title="Cookie Policy" />
      <main className="main-wrapper">
        <HeaderOne />

        <BreadCrumbOne
          subtitle=""
          title={pageContent.title}
          page="Cookie-policy"
        />

        <div className="section  " style={{ padding: "0px 0 0px" }}>
          <SectionTitle textAlignment="" textColor="mb--90" />
          <div className="container">
            <div className="process-work">
              <div className="">
                {Array.isArray(pageContent.content) ? (
                  pageContent.content.map((item, index) => (
                    <div key={index} className="mb-4">
                      <h4 className="title">{item.title}</h4>
                      <p className="">{item.subcontent}</p>
                    </div>
                  ))
                ) : (
                  <p className="px-2">{pageContent.content}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        <div style={{ paddingTop: "0px" }}>
          <CounterUpTwo />
        </div>
        <AboutThree />
        <div className="container " style={{ paddingTop: "100px" }}>
          <SectionTitle
            subtitle="Our Clients"
            title="Our valued partners"
            textAlignment="heading-left mb--40"
            textColor=""
          />
        </div>
        <ClientSlider
          itemShow={4}
          colSize="col-md-3"
          columnGap="row-35"
          parentClass="your-custom-class"
        />
        <FooterOne parentClass="" />
      </main>
    </>
  );
};

export default CookiePolicyPage;
